const MAX_VENUE_NAME_LENGTH = 60;

interface VenueLocationParams {
  /** Borough or district level (e.g. "Wandsworth", "Soho"). Takes priority over route as the area segment. */
  sublocality?: string | null;
  /** Road/street name (e.g. "Gray's Inn Road"). Used as area segment when sublocality is absent. */
  route?: string | null;
  /** UK postal town (e.g. "London", "Manchester"). */
  postalTown?: string | null;
  /** Google locality / city (e.g. "London", "Oxford"). */
  locality?: string | null;
  /** Last segment of Google vicinity address — fallback city label. */
  cityLocation?: string | null;
  /** Full formatted address — last-resort fallback when no other location field is set. */
  address?: string | null;
  /** Optional venue type appended after a bullet separator (e.g. "Cafe", "Restaurant"). */
  businessType?: string | null;
}

/**
 * Builds the location subtitle shown on venue detail pages for all venues.
 *
 * Format examples:
 *   "Soho, London • Cafe"
 *   "Gray's Inn Road, London • Coffee Shop"
 *   "London • Restaurant"  (no area-level data)
 *   "Soho, London"         (no businessType passed)
 *   ""                     (no data at all)
 *
 * Area priority (most → least specific): sublocality → route
 * City priority (most → least specific): postalTown → locality → cityLocation
 */
export function getVenueLocationSubtitle(params: VenueLocationParams): string {
  const {
    sublocality,
    route,
    postalTown,
    locality,
    cityLocation,
    address,
    businessType,
  } = params;

  const area = sublocality || route || null;
  const city = postalTown || locality || cityLocation || null;
  const locationSegment = [area, city].filter(Boolean).join(", ") || address || null;

  return [locationSegment, businessType].filter(Boolean).join(" • ");
}

interface VenueTitleParams {
  name: string;
  /** Non-empty brandId means the venue is part of a chain/brand. */
  brandId?: string | number | null;
  /** Road/street name (e.g. "Gray's Inn Road", "Oxford Street"). */
  route?: string | null;
  /** Borough or district level (e.g. "Wandsworth", "Cheetham Hill"). */
  sublocality?: string | null;
  /** UK postal town (e.g. "London", "Manchester"). */
  postalTown?: string | null;
  /** Google locality / city (e.g. "London", "Oxford"). */
  locality?: string | null;
  /** Last segment of Google vicinity address — fallback city label. */
  cityLocation?: string | null;
  /** Full formatted address — last-resort fallback when no other location field is set. */
  address?: string | null;
}

/**
 * Returns the SEO page title for a venue details page.
 *
 * For chain/brand venues the street and city are appended so that
 * individual branches are uniquely identifiable in search results:
 *   "Costa Coffee - Gray's Inn Road - London Accessibility | Sociability"
 *   "Costa Coffee - London Accessibility | Sociability"  (no route available)
 *
 * Location priority (most → least specific):
 *   city:  sublocality → postalTown → locality → cityLocation
 *
 * For independent venues the location is omitted:
 *   "Farzi Cafe Accessibility | Sociability"
 */
export function getVenuePageTitle(
  params: VenueTitleParams,
  suffix: string,
): string {
  const {
    name,
    brandId,
    route,
    sublocality,
    postalTown,
    locality,
    cityLocation,
    address,
  } = params;

  const truncated =
    name.length > MAX_VENUE_NAME_LENGTH
      ? name.substring(0, MAX_VENUE_NAME_LENGTH) + "..."
      : name;

  const isChain = !!brandId;

  // if (isChain) {
    const seoCity =
      sublocality || postalTown || locality || cityLocation || null;
    const locationParts = [
      ...(route ? [route] : []),
      ...(seoCity ? [seoCity] : []),
    ];

    if (locationParts.length === 0 && address) {
      locationParts.push(address);
    }

    if (locationParts.length > 0) {
      return `${truncated} - ${locationParts.join(" - ")} ${suffix}`;
    }
  // }

  return `${truncated} ${suffix}`;
}
